import { computed, ref, Ref, watch } from "vue";
import { useTocPathAndTitleQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { HtmlDocument } from "@/abilities/documentation/types/documentation.ts";

export function useDocumentationQuery(contentMapId: Ref<string>, topicId: Ref<string | undefined>) {
    const loadingDocument = ref<boolean>(true);
    const title = ref<string>();
    const path = ref<string>();
    const childPath = ref<string>();
    const childPathForBreadCrumb = ref<string>();
    const document = ref<HtmlDocument>();

    const { result, error, loading } = useTocPathAndTitleQuery(
        computed(() => ({ contentId: topicId.value, rootContentId: contentMapId.value })),
        computed(() => ({ enabled: !!topicId.value, fetchPolicy: "no-cache" }))
    );

    const pathNotFound = computed(() => {
        return !loading.value && topicId.value !== "-" && result.value?.contentInContext?.length !== 1;
    });

    watch(result, () => {
        if (
            result.value?.contentInContext?.length == 1 &&
            result.value.contentInContext[0].node?.__typename === "Topic"
        ) {
            //can't use a variable or else typescript will complain
            title.value = result.value.contentInContext[0].node.teasers?.title ?? "";
            path.value = result.value.contentInContext[0].path;
            //unfortunately childPath is returned even when there is no child node
            childPath.value = result.value.contentInContext[0].hasChildren
                ? result.value.contentInContext[0].childPath
                : undefined;
            childPathForBreadCrumb.value = result.value.contentInContext[0].childPath;
            const next = result.value.contentInContext[0].next;
            const previous = result.value.contentInContext[0].previous;
            const { url, mimeType, locale } = result.value.contentInContext[0].node;
            const rootContentMap = result.value.contentInContext[0].node.useContext?.rootContentMap;

            document.value = {
                mimeType: mimeType!,
                url,
                version: rootContentMap?.version,
                locale: locale ?? undefined,
                versionLabel: rootContentMap?.versionLabel ?? undefined,
                next: next ? { title: next.teasers?.title ?? "", id: next.id } : undefined,
                previous: previous ? { title: previous.teasers?.title ?? "", id: previous.id } : undefined,
            };

            loadingDocument.value = false;
        }
    });

    return { loadingDocument, title, path, document, pathNotFound, error, childPath, childPathForBreadCrumb };
}
